import React, { Dispatch, SetStateAction, useState } from 'react';
import { Button, CircularProgress, Stack, Tooltip } from '@mui/material';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import StyledModal from '../../../Components/GenericModal/StyledModal';
import { StyledSnackbarProps } from '../../../Components/CustomUIElements/StyledSnackbar';
import { classes } from '../styles';
import ModalFooterButtons from '../../../Components/GenericModal/ModalFooterButtons';
import { theme } from '../../../../ThemeContext/ThemeObject';
import { useMutation, useQuery } from '@tanstack/react-query';
import Api from '../../../Templates/API';
import Search from '../../../SmartForms/Search';
import QuestionFieldViewer from '../../../Components/Utilities/QuestionFieldViewer';
import { formatUserPermissions } from '../../../Components/Utilities/formatUserPermissions';
import SearchParameters from './Icons/SearchParameters.svg';
import EmptyFolder from './Icons/EmptyFolder.svg';
import { sharedClasses } from '../../../Components/CustomUIElements/sharedClasses';
import NewQuestion from '../../../Templates/Question/Modals/NewQuestion';
import { TQuestion } from '../../../Templates/types';

export default function QuestionLibraryModal({
  modalsOpen,
  setModalsOpen,
  setSnackbar,
  getQuestions,
  jobId
}: {
  modalsOpen: Record<string, boolean>;
  setModalsOpen: Dispatch<SetStateAction<Record<string, boolean>>>;
  setSnackbar: StyledSnackbarProps['setSnackbarState'];
  getQuestions: () => void;
  jobId: number;
}) {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedQuestions, setSelectedQuestions] = useState<number[]>([]);
  const [showSelectedQuestions, setShowSelectedQuestions] = useState<boolean>(false);
  const [newQuestionModalOpen, setNewQuestionModalOpen] = useState<boolean>(false);
  const permissions = formatUserPermissions();
  const { data: questions, isLoading: loadingQuestions } = useQuery<TQuestion[]>({
    queryKey: ['questions'],
    queryFn: async () => {
      const { res } = await Api.getQuestions({ override_limit: true, limit: 100 });
      return res.library_fields as TQuestion[];
    },
    onError: () =>
      setSnackbar({
        message: `There was an error getting question library data`,
        state: 'error'
      })
  });

  const { mutate: importQuestions, isLoading: importingQuestions } = useMutation({
    mutationFn: () =>
      Api.importQuestions({
        job_id: jobId,
        library_field_ids: selectedQuestions
      }),
    onError: (error) =>
      setSnackbar({
        message: `There was an error importing questions, ${error}`,
        state: 'error'
      }),
    onSuccess: () => {
      getQuestions();
      handleClose();
    }
  });

  const handleClose = () => {
    setModalsOpen((prev) => ({ ...prev, questionLibrary: false }));
  };

  const noQuestionInLibrary = questions?.length === 0;

  const filteredQuestions = questions?.filter(
    (question) =>
      question.library_field_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      question.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderQuestionStatus = (
    question: TQuestion,
    type: 'required' | 'rateable' | 'confidential'
  ) => {
    const isEnabled = question[type];
    return isEnabled ? (
      <CheckCircleIcon sx={{ color: theme.palette.success.secondary }} />
    ) : (
      <CancelIcon sx={{ color: theme.palette.error.main }} />
    );
  };

  const renderEmptyState = (message: string, icon: string) => (
    <Stack sx={classes.questionLibraryModalEmptyStateContainer}>
      <Stack component="img" src={icon} sx={{ width: '10rem' }} />
      <Stack sx={classes.questionLibraryModalEmptyStateMessage}>{message}</Stack>
      {noQuestionInLibrary && permissions?.Entities['Create / Edit / Delete Question Library'] && (
        <Button
          sx={sharedClasses.genericButtonSecondary}
          onClick={() => setNewQuestionModalOpen(true)}
          id={`create-question-library-from-modal-button`}
        >
          Create question
        </Button>
      )}
    </Stack>
  );

  const renderQuestionList = () => {
    if (loadingQuestions) {
      return (
        <Stack sx={classes.questionLibraryModalEmptyStateContainer}>
          <CircularProgress size={100} sx={{ color: theme.palette.action.main }} />
        </Stack>
      );
    }

    if (noQuestionInLibrary) {
      return renderEmptyState('No questions available in the library', EmptyFolder);
    }

    if (!filteredQuestions?.length) {
      return renderEmptyState(
        'There are no questions that match your search parameters',
        SearchParameters
      );
    }

    const filteredSelectedQuestions = filteredQuestions.filter((question) => {
      if (showSelectedQuestions) {
        return selectedQuestions.includes(question.id);
      }
      return true;
    });

    if (showSelectedQuestions && !filteredSelectedQuestions.length) {
      return renderEmptyState('No selected questions available', EmptyFolder);
    }

    return filteredSelectedQuestions.map((question) => {
      const isSelected = selectedQuestions.includes(question.id);
      return (
        <Stack key={question.id} sx={{ borderRadius: '6px', background: '#F3F3F3', padding: 2 }}>
          <Stack sx={{ flexDirection: 'row', columnGap: 2, alignItems: 'start' }}>
            <Stack
              sx={{
                background: isSelected ? '#15C7C0' : 'white',
                borderRadius: '4px',
                border: '3px solid #D0D0D0',
                padding: isSelected ? 0 : 1,
                margin: 1,
                cursor: 'pointer',
                ...(isSelected && {
                  color: 'white',
                  borderColor: '#15C7C0'
                })
              }}
              onClick={() => {
                if (isSelected) {
                  setSelectedQuestions(selectedQuestions.filter((id) => id !== question.id));
                } else {
                  setSelectedQuestions([...selectedQuestions, question.id]);
                }
              }}
              id={`add-question-library-modal-checkbox-${question.id}`}
            >
              {isSelected && (
                <CheckIcon sx={{ fontSize: '16px', strokeWidth: 2, stroke: 'white' }} />
              )}
            </Stack>
            <Stack sx={{ flexGrow: 1 }}>
              <Stack sx={classes.questionLibraryModalListHeader}>
                {question.library_field_name}
              </Stack>
              <QuestionFieldViewer question={question} />
            </Stack>
            <Stack sx={{ alignItems: 'center', rowGap: 1 }}>
              <Stack sx={classes.questionLibraryModalListHeader}>Required</Stack>
              {renderQuestionStatus(question, 'required')}
            </Stack>
            <Stack sx={{ alignItems: 'center', rowGap: 1 }}>
              <Stack sx={classes.questionLibraryModalListHeader}>Rateable</Stack>
              {renderQuestionStatus(question, 'rateable')}
            </Stack>
            <Stack sx={{ alignItems: 'center', rowGap: 1 }}>
              <Stack sx={classes.questionLibraryModalListHeader}>Confidential</Stack>
              {renderQuestionStatus(question, 'confidential')}
            </Stack>
          </Stack>
        </Stack>
      );
    });
  };

  return (
    <StyledModal
      isOpen={modalsOpen.questionLibrary}
      label="Question library modal"
      handleClose={handleClose}
      styleOverrides={{ ...classes.modalStyleOverrides, height: '80vh', paddingTop: '56px' }}
      modalId={`add-question-library-modal`}
      closeIconId={`add-question-library-modal-close-icon`}
    >
      <Stack sx={classes.modalContainer}>
        <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack sx={{ ...classes.actionModalHeader, margin: 'unset' }}>Question library</Stack>
          {permissions?.Entities?.['Create / Edit / Delete Question Library'] && (
            <Tooltip placement="top" title="Open Question Library page" arrow>
              <OpenInNewIcon
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  window.open(`${window.location.origin}/admin/templates`, '_blank');
                }}
                id={`open-question-library-page-link-icon`}
              />
            </Tooltip>
          )}
        </Stack>
        <Search
          idLabel="question-library-modal-search"
          placeholder="Search a question"
          setSearch={(title) => setSearchQuery(title)}
        />
        <Stack sx={{ overflow: 'auto', rowGap: 2, flexGrow: 1 }}>{renderQuestionList()}</Stack>
        <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack
            sx={{
              color: theme.palette.action.main,
              fontWeight: 'bold',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' }
            }}
            onClick={() => setShowSelectedQuestions(!showSelectedQuestions)}
            id={`add-question-library-modal-show-selected-questions-button`}
          >
            {`Show ${showSelectedQuestions ? 'all' : 'selected'} questions`}
          </Stack>
          <ModalFooterButtons
            primaryButtonText={`Add${selectedQuestions.length ? ` (${selectedQuestions.length})` : ''}`}
            secondaryButtonText="Cancel"
            secondaryButtonCallback={handleClose}
            primaryButtonMinWidth="95px"
            primaryButtonCallback={importQuestions}
            primaryButtonDisabled={selectedQuestions.length === 0}
            isLoading={importingQuestions}
            stylesOverride={{ width: 'unset' }}
            primaryButtonID={`add-question-library-modal-add-questions-button`}
            secondaryButtonID={`add-question-library-modal-cancel-button`}
          />
        </Stack>
      </Stack>
      {newQuestionModalOpen && (
        <NewQuestion
          modalOpen={newQuestionModalOpen}
          onClose={() => setNewQuestionModalOpen(false)}
          setSnackbar={setSnackbar}
          selectedQuestion={null}
        />
      )}
    </StyledModal>
  );
}
